/**
 * MessageBubble Component - Bolha individual de mensagem
 * @module features/chat/components/MessageBubble
 *
 * Componente para exibir uma mensagem no chat com estilos
 * diferenciados para usuario e assistente.
 *
 * T002-FE: Interface de Chat Conversacional
 */

import { cn } from '@/lib/utils';
import type { MessageBubbleProps } from '../types/chat.types';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Bolha de mensagem com estilo diferenciado por role
 *
 * Features:
 * - Alinhamento a direita para usuario, esquerda para assistente
 * - Cores diferenciadas (primary para user, muted para assistant)
 * - Suporte a streaming cursor
 * - Animacoes de entrada com suporte a reduced motion
 * - Acessibilidade com role="article" e aria-label
 *
 * @example
 * ```tsx
 * <MessageBubble
 *   message={{ id: '1', role: 'user', content: 'Ola!', createdAt: new Date() }}
 * />
 * ```
 */
export function MessageBubble({ message, isStreaming = false }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div
      data-testid="message-wrapper"
      className={cn(
        'flex w-full animate-fade-in motion-reduce:animate-none',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <article
        aria-label={isUser ? 'Mensagem do usuario' : 'Mensagem do assistente'}
        data-testid="message-bubble"
        className={cn(
          'max-w-[80%] rounded-lg px-4 py-2 whitespace-pre-wrap break-words',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-none'
            : 'bg-muted text-foreground rounded-bl-none'
        )}
      >
        {message.content}
        {isStreaming && (
          <span
            data-testid="streaming-cursor"
            className="inline-block w-2 h-4 ml-1 bg-current animate-pulse"
            aria-hidden="true"
          />
        )}
      </article>
    </div>
  );
}

export default MessageBubble;
